import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import { Card, CardContent, Typography, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  CartesianGrid,
} from "recharts";

interface SafetyScoreGraphProps {
  tableGraphData?: any;
}

const VehicleDashboardWarningGraph: React.FC<SafetyScoreGraphProps> = ({
  tableGraphData,
}) => {
  const theme = useTheme();

  // Sort data in ascending order (oldest first)
  const sortedData = useMemo(() => {
    if (!tableGraphData || !tableGraphData.dayWiseWarningResponseList)
      return [];
    return [...tableGraphData.dayWiseWarningResponseList].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [tableGraphData]);

  // Use ref for windowStart.
  const windowStart = useRef(0);
  const [chartData, setChartData] = useState<any[]>([]);
  const [dataRangeLabel, setDataRangeLabel] = useState("");
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  // When sortedData changes, set windowStart to show last 6 days.
  useEffect(() => {
    if (sortedData.length > 0) {
      windowStart.current =
        sortedData.length > 6 ? sortedData.length - 6 : 0;
    } else {
      windowStart.current = 0;
    }
    updateChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedData]);

  // Helper function to update chart data, label, and navigation flags.
  const updateChartData = () => {
    if (sortedData.length > 0) {
      const start = windowStart.current;
      const windowData = sortedData.slice(start, start + 6);

      // Compute date range label.
      const startDate = new Date(windowData[0].date);
      const endDate = new Date(windowData[windowData.length - 1].date);
      const startLabel = startDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
      const endLabel = endDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
      const rangeLabel =
        startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
      setDataRangeLabel(rangeLabel);

      // Map windowData to chartData.
      const transformedData = windowData.map(
        (item: { date: string; warningCount: number }) => {
          const dateObj = new Date(item.date);
          const day = dateObj.getDate();
          const month = dateObj.toLocaleString("default", { month: "short" });
          return {
            name: `${day} ${month}`,
            value: Math.round(item.warningCount),
          };
        }
      );
      setChartData(transformedData);

      // Navigation flags.
      setHasPrevious(start > 0);
      setHasNext(start + 6 < sortedData.length);
    } else {
      setChartData([]);
      setDataRangeLabel("");
      setHasPrevious(false);
      setHasNext(false);
    }
  };

  const handlePrevious = () => {
    if (hasPrevious) {
      windowStart.current = Math.max(0, windowStart.current - 1);
      updateChartData();
    }
  };

  const handleNext = () => {
    if (hasNext) {
      windowStart.current = Math.min(
        sortedData.length - 6,
        windowStart.current + 1
      );
      updateChartData();
    }
  };

  return (
    <Card className="w-full h-full flex flex-col items-start">
      <Typography
        variant="button"
        color="secondary"
        fontWeight={400}
        className="mb-2"
      >
        Day Wise Warnings
      </Typography>
      <CardContent className="border rounded-xl border-[#F0EFF8] w-full">
        <div className="flex gap-2 pt-4">
          <div className="relative" style={{ left: "8px", top: "105px" }}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                textAlign: "center",
              }}
            >
              Warning Counts
            </Typography>
          </div>
          <div className="flex-1 min-h-[200px] max-h-[400px]">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient
                    id="safetyGradient"
                    x1="0"
                    y1="1"
                    x2="0"
                    y2="0"
                  >
                    <stop
                      offset="0%"
                      stopColor={`${theme.palette.primary.main}00`}
                    />
                    <stop
                      offset="25%"
                      stopColor={`${theme.palette.primary.main}40`}
                    />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" />
                <YAxis domain={[0, "dataMax"]} />
                <CartesianGrid strokeDasharray="3 3" />
                <Tooltip
                  formatter={(value: number, name: string) => [`${value}`, "Warning Count"]}
                  cursor={false}
                  contentStyle={{
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                  labelStyle={{ color: theme.palette.text.primary }}
                  itemStyle={{ color: theme.palette.text.primary }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  fill="url(#safetyGradient)"
                  fillOpacity={1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        {sortedData.length > 0 ? (
          <div className="w-full flex items-center justify-center gap-4 mt-2">
            <IconButton
              onClick={handlePrevious}
              disabled={!hasPrevious}
              size="small"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <Typography variant="caption" color="textPrimary" align="center">
              {dataRangeLabel}
            </Typography>
            <IconButton onClick={handleNext} disabled={!hasNext} size="small">
              <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </div>
        ) : (
          <div className="w-full flex justify-center">
            <Typography
              variant="caption"
              color="textPrimary"
              align="center"
              className="mt-2 w-full"
            >
              Warning Counts Data
            </Typography>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VehicleDashboardWarningGraph;